import { useState, useEffect } from 'react';
import { Wifi, WifiOff, RefreshCw, Cloud, CloudOff, Check } from 'lucide-react';
import { useOffline } from '../contexts/OfflineContext';

export default function OfflineIndicator() {
  const { isOnline, pendingCount, isSyncing, lastSyncResult, manualSync } =
    useOffline();
  const [showBanner, setShowBanner] = useState(false);
  const [showSyncSuccess, setShowSyncSuccess] = useState(false);

  useEffect(() => {
    if (!isOnline) {
      setShowBanner(true);
    } else if (showBanner) {
      const timer = setTimeout(() => setShowBanner(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [isOnline, showBanner]);

  useEffect(() => {
    if (lastSyncResult?.success && lastSyncResult.synced > 0) {
      setShowSyncSuccess(true);
      const timer = setTimeout(() => setShowSyncSuccess(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [lastSyncResult]);

  const handleSync = async () => {
    if (!isSyncing && isOnline && pendingCount > 0) {
      await manualSync();
    }
  };

  if (!showBanner && !pendingCount && !showSyncSuccess && isOnline) {
    return null;
  }

  return (
    <>
      {/* Offline Banner */}
      {!isOnline && (
        <div className="fixed top-0 left-0 right-0 z-[9999] bg-amber-500 text-white shadow-lg animate-slide-down">
          <div className="max-w-7xl mx-auto px-4 py-2 sm:px-6">
            <div className="flex items-center justify-center gap-2">
              <WifiOff className="w-4 h-4" />
              <span className="text-sm font-medium">
                Sin conexión - Trabajando en modo offline
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Connection Restored Banner */}
      {isOnline && showBanner && (
        <div className="fixed top-0 left-0 right-0 z-[9999] bg-emerald-500 text-white shadow-lg animate-slide-down">
          <div className="max-w-7xl mx-auto px-4 py-2 sm:px-6">
            <div className="flex items-center justify-center gap-2">
              <Wifi className="w-4 h-4" />
              <span className="text-sm font-medium">Conexión restaurada</span>
              {pendingCount > 0 && (
                <span className="text-xs opacity-90">
                  - Sincronizando {pendingCount} acciones...
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Sync Success Toast */}
      {showSyncSuccess && (
        <div className="fixed bottom-4 right-4 z-[9999] bg-emerald-500 text-white px-4 py-3 rounded-xl shadow-lg animate-slide-up flex items-center gap-2">
          <Check className="w-4 h-4" />
          <span className="text-sm font-medium">
            {lastSyncResult.synced} acciones sincronizadas
          </span>
        </div>
      )}

      {/* Pending Actions Indicator */}
      {pendingCount > 0 && isOnline && !showBanner && (
        <div className="fixed bottom-4 right-4 z-[9998]">
          <button
            onClick={handleSync}
            disabled={isSyncing}
            className={`
              flex items-center gap-2 px-4 py-2 rounded-xl shadow-lg
              ${
                isSyncing
                  ? 'bg-blue-500 text-white cursor-wait'
                  : 'bg-amber-500 hover:bg-amber-600 text-white cursor-pointer'
              }
              transition-all duration-200
            `}
          >
            {isSyncing ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" />
                <span className="text-sm font-medium">Sincronizando...</span>
              </>
            ) : (
              <>
                <Cloud className="w-4 h-4" />
                <span className="text-sm font-medium">
                  {pendingCount} pendiente{pendingCount > 1 ? 's' : ''}
                </span>
              </>
            )}
          </button>
        </div>
      )}

      {/* Offline Pending Indicator */}
      {pendingCount > 0 && !isOnline && (
        <div className="fixed bottom-4 right-4 z-[9998]">
          <div className="flex items-center gap-2 px-4 py-2 bg-gray-700 text-white rounded-xl shadow-lg">
            <CloudOff className="w-4 h-4" />
            <span className="text-sm font-medium">
              {pendingCount} pendiente{pendingCount > 1 ? 's' : ''} de
              sincronizar
            </span>
          </div>
        </div>
      )}
    </>
  );
}
